import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { InvoiceDialog } from "@/components/crm/InvoiceDialog";
import { api, errorMessage } from "@/lib/api";
import { INV_LABEL, InvoiceBadge, PageHead, ScopeFilters, inpI, qs, usePeople } from "@/lib/crm";
import { money } from "@/lib/pricing";

export default function Invoices() {
  const people = usePeople();
  const [f, setF] = useState({});
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get(`/invoices?${qs(f)}`).then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e))), [f]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const openInv = async (id) => { try { setOpen((await api.get(`/invoices/${id}`)).data); } catch (e) { toast.error(errorMessage(e)); } };
  return (
    <div className="space-y-8" data-testid="invoices-page">
      <PageHead kicker="CRM" title="Invoices" sub="Deposits and final balances. Record payments by hand." />
      <div className="flex flex-wrap items-center gap-2">
        <ScopeFilters people={people} f={f} setF={setF} prefix="invoices" />
        <select data-testid="invoices-status-filter" value={f.status || ""} onChange={(e) => setF({ ...f, status: e.target.value })} className={`${inpI} w-40`}>
          <option value="">All statuses</option>
          {Object.entries(INV_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" data-testid="invoices-overdue-filter" checked={!!f.overdue} onChange={(e) => setF({ ...f, overdue: e.target.checked || "" })} /> Overdue only</label>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-800">
        <table className="w-full min-w-[900px]" data-testid="invoices-table">
          <thead><tr className="border-b border-slate-800 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {["Invoice", "Customer / project", "Rep", "Issued", "Due", "Status", "Amount", "Balance"].map((h) => <th key={h} className="px-5 py-3">{h}</th>)}</tr></thead>
          <tbody>{rows?.map((inv, i) => (
            <tr key={inv.id} data-testid={`invoices-row-${i}`} onClick={() => openInv(inv.id)} className="cursor-pointer border-b border-slate-800/60 text-sm hover:bg-amber-500/5">
              <td className="px-5 py-3"><p className="font-mono-num text-slate-100" data-testid={`invoices-number-${i}`}>{inv.number}</p><p className="text-xs text-slate-500">{inv.label}</p></td>
              <td className="px-5 py-3 text-slate-300">{inv.customer_name}<br /><span className="text-xs text-slate-500">{inv.project_name} · {inv.quote_number}</span></td>
              <td className="px-5 py-3 text-slate-400">{inv.rep_name}</td>
              <td className="px-5 py-3 text-slate-400">{inv.issue_date}</td>
              <td className="px-5 py-3 text-slate-400">{inv.due_date || "At shipment"}</td>
              <td className="px-5 py-3"><InvoiceBadge inv={inv} testId={`invoices-status-${i}`} /></td>
              <td className="px-5 py-3 font-mono-num text-slate-300">{money(inv.amount)}</td>
              <td className="px-5 py-3 font-mono-num font-semibold text-slate-100" data-testid={`invoices-balance-${i}`}>{money(inv.balance)}</td>
            </tr>))}
          </tbody>
        </table>
        {rows?.length === 0 && <p data-testid="invoices-empty" className="py-14 text-center text-sm text-slate-500">No invoices.</p>}
      </div>
      {open && <InvoiceDialog invoice={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
